import React, { useMemo } from "react";
import { withTranslation } from "react-i18next";
import { Button, CircularProgress, Grid } from "@mui/material";
import PropTypes from "prop-types";
import * as Yup from "yup";
import { useFormik } from "formik";

import Modal from "Components/Common/Modal";
import SelectField from "Components/Common/SelectField";
import Backdrop from "Components/Common/Backdrop";

import {
  useGetDeveloperBuildingsQuery,
  usePutUpdateFireSafetyApplicationStatusMutation,
  usePostFireSafetyApplicationBuildingAssignMutation,
} from "apis/AuditSchedular";
import InputFieldText from "Components/Common/InputFieldText";
import { applicationApprovalStatusTypes } from "common/constant";

const otherBuildingId = 0;

const DeveloperBuilding = ({
  t,
  handleClose,
  applicationId,
  buildingName,
  isBuildingNotAssignedBySrManager,
}) => {
  const { data: { data: buildingsData } = {}, isLoading } =
    useGetDeveloperBuildingsQuery();

  const [updateStatus, { isLoading: statusLoading }] =
    usePutUpdateFireSafetyApplicationStatusMutation();

  const [assignBuilding, { isLoading: isBuildingAssigning }] =
    usePostFireSafetyApplicationBuildingAssignMutation();

  const formik = useFormik({
    initialValues: {
      buildingId: buildingName,
      otherBuildingName: "",
    },
    validationSchema: Yup.object({
      buildingId: Yup.string().required("Building is required"),
      otherBuildingName: Yup.string().test(
        "isOtherBuildingNameRequired",
        "",
        function () {
          const [{ value: current = null } = {}] = this.from || [];

          if (
            current.buildingId === otherBuildingId &&
            !current.otherBuildingName
          ) {
            return this.createError({
              message: "Building name is required",
            });
          }

          if (
            current.buildingId === otherBuildingId &&
            buildingsData?.some(
              ({ name }) =>
                name.toLowerCase() === current.otherBuildingName.toLowerCase()
            )
          ) {
            return this.createError({
              message: "Building name should be unique",
            });
          }
          return true;
        }
      ),
    }),
    onSubmit: async ({ buildingId, otherBuildingName }, { resetForm }) => {
      try {
        if (isBuildingNotAssignedBySrManager) {
          await assignBuilding({
            buildingInformationId: applicationId,
            name: buildingId || otherBuildingName,
          });
        } else {
          await updateStatus({
            buildingInformationId: applicationId,
            buildingName: buildingId || otherBuildingName,
            status: applicationApprovalStatusTypes["Approved"],
          });
        }
        handleClose(true);
        resetForm();
      } catch (error) {}
    },
  });

  const {
    values: { buildingId },
    handleSubmit,
  } = formik;

  const updatedBuildingOptions = useMemo(
    () => [
      ...(buildingsData?.map(({ name }) => ({ label: name, value: name })) ??
        []),
      { value: 0, label: "Other" },
    ],
    [buildingsData]
  );

  if (isLoading) {
    return <Backdrop open />;
  }

  return (
    <Modal
      isOpen
      handleClose={() => handleClose(false)}
      maxWidth="sm"
      title="Audit Schedule"
    >
      <Grid container className="mt-3">
        <Grid item xs={12}>
          <SelectField
            label={t("Building") + "*"}
            name="buildingId"
            formik={formik}
            options={updatedBuildingOptions || []}
          />
        </Grid>

        {buildingId === otherBuildingId && (
          <Grid item xs={12}>
            <InputFieldText
              label={t("Building Name") + "*"}
              name="otherBuildingName"
              formik={formik}
              placeholder="Enter Building Name"
            />
          </Grid>
        )}

        <Grid item className="d-block ms-auto">
          <Button
            color="primary"
            size="small"
            variant="contained"
            onClick={handleSubmit}
            endIcon={
              (statusLoading || isBuildingAssigning) && (
                <CircularProgress size={20} />
              )
            }
            disabled={statusLoading || isBuildingAssigning}
          >
            Submit
          </Button>
        </Grid>
      </Grid>
    </Modal>
  );
};

DeveloperBuilding.propTypes = {
  t: PropTypes.func,
  handleClose: PropTypes.func,
  isBuildingNotAssignedBySrManager: PropTypes.bool,
  buildingName: PropTypes.string,
  applicationId: PropTypes.number,
};

export default withTranslation()(DeveloperBuilding);
